<template>
  <div class="full-height">
    <spinner v-if="loading" />

    <div v-else-if="localConfig && localConfig.campaignData" class="full-height">
      <card>
        <!-- Name, type -->
        <div class="row">
          <!-- Name -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.name')"
                :validation="['required']"
                ref="data_name"
                v-if="isCreate || isEdit"
                v-model="name" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.name')"
                :value="name"
                v-else />
            </div>
          </div>

          <!-- Campaign type selection -->
          <div class="slds-form-element slds-size--medium col">
            <campaign-type-selector
              ref="data_campaignType"
              v-if="isCreate && localConfig.campaignData.type.options.length > 1"
              v-model="campaignType"
              :options="localConfig.campaignData.type.options"
              :validation="['required']" />

            <output-field :label="$t('campaignDesigner.campaignSetup.type.label')" type="custom" v-else>
              <span class="slds-swatch type-color-container" :style="`background:${campaignType.color ? campaignType.color: '#FFFFFF'};`" />
              <span>{{campaignType.label}}</span>
            </output-field>
          </div>
        </div>

        <!-- Target Source, Market -->
        <div class="row">
          <!-- Market -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              can-remove-elements
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.markets.options"
              :validation="['required']"
              labels-prefix="markets"
              ref="data_markets"
              v-if="isCreate || isEdit"
              v-model="markets" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.markets.options"
              :value="markets"
              labels-prefix="markets"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- Target Source, Deduplication parameter -->
        <div class="row">
          <!-- Target Source -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.targetSource.label')"
              :options="localConfig.campaignData.targetSource.options"
              :validation="['required']"
              labels-prefix="campaignDesigner.campaignSetup.targetSource"
              show-empty-value
              ref="data_targetSource"
              v-if="isCreate || isEdit"
              v-model="targetSource" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.targetSource.label')"
              :options="localConfig.campaignData.targetSource.options"
              :value="targetSource"
              labels-prefix="campaignDesigner.campaignSetup.targetSource"
              type="picklist"
              v-else />
          </div>

          <!-- Deduplication parameter -->
          <div class="slds-form-element slds-size--medium col">
            <template v-if="!targetSource">
              <picklist
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :validation="['required']"
                disabled
                show-empty-value
                v-if="isCreate || isEdit"
                v-model="deduplicationParameter" />

              <span v-else>{{nbsp}}</span>
            </template>
            <template v-else-if="targetSource === 'contacts'">
              <output-field
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                type="picklist"
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :value="deduplicationParameter" />
            </template>
            <template v-else-if="targetSource === 'contacts_leads'">
              <picklist
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :validation="['required']"
                ref="data_deduplication"
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                v-if="isCreate || isEdit"
                v-model="deduplicationParameter" />

              <output-field
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                type="picklist"
                :value="deduplicationParameter"
                v-else />
            </template>
          </div>
        </div>

        <!-- Start date, End date -->
        <div class="row">
          <!-- Start date -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :validation="[{name: 'is-date-before', param: endDate}, 'is-date-after-today', 'required']"
                ref="data_startDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="startDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :value="startDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- End date -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :validation="['is-date-after-today']"
                ref="data_endDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="endDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :value="endDate"
                type="date"
                v-else />
            </div>
          </div>
        </div>

        <!-- Brands, Channels -->
        <div class="row">
          <!-- Brands -->
          <div class="slds-form-element col slds-size--medium">
            <picklist
              :disabled="brandDisabled"
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :options="localConfig.campaignData.campaignBrands.options"
              :validation="brandDisabled ? [] : ['required']"
              labels-prefix="brands"
              multipicklist
              ref="data_brands"
              v-if="isCreate || isEdit"
              v-model="campaignBrands" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :options="localConfig.campaignData.campaignBrands.options"
              :value="campaignBrands"
              labels-prefix="brands"
              type="multipicklist"
              v-else />
          </div>

          <!-- Channels -->
          <div class="slds-form-element col slds-size--medium">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :validation="['required']"
              labels-prefix="channels"
              multipicklist
              ref="data_channels"
              v-if="isCreate || isEdit"
              v-model="campaignChannel" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :value="campaignChannel"
              labels-prefix="channels"
              type="multipicklist"
              v-else />
          </div>
        </div>

        <!-- Segment Type, Masterkey -->
        <div class="row">
          <!-- Segment Type -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              ref="data_segmentType"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="segmentType" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              :value="segmentType"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              type="picklist"
              v-else />
          </div>

          <!-- Masterkey -->
          <div class="slds-form-element slds-size--medium col" v-if="masterKeys">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :options="masterKeys"
              show-empty-value
              ref="data_masterkey"
              searchable
              v-if="isCreate || isEdit"
              v-model="masterKey" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :value="masterKey"
              type="custom"
              v-else>

              <span v-if="masterKeys && masterKeys.length && masterKey">{{masterKey.length && masterKeys.find(mk => mk.value === masterKey).label}}</span>
            </output-field>
          </div>
        </div>

        <!-- CCDB Report Enabled, CCDB Report Type -->
        <div class="row">
          <!-- CCDB Report Enabled -->
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="ccdbReportEnabled" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              :value="ccdbReportEnabled"
              type="checkbox"
              v-else />
          </div>

          <!-- CCDB Report Type -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.ccdbReportType.label')"
              :options="localConfig.campaignData.ccdbReportType.options"
              labels-prefix="campaignDesigner.campaignSetup.ccdbReportType"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="ccdbReportType" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.ccdbReportType.label')"
              :options="localConfig.campaignData.ccdbReportType.options"
              :value="ccdbReportType"
              labels-prefix="campaignDesigner.campaignSetup.ccdbReportType"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- Jeep Wave -->
        <div class="row">
          <!-- Jeep Wave -->
          <div class="slds-form-element col slds-size--medium" v-if="campaignBrands.includes('057')">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.jeepWave')"
              :options="localConfig.campaignData.jeepWave.options"
              multipicklist
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="jeepWave" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.jeepWave')"
              :options="localConfig.campaignData.jeepWave.options"
              :value="jeepWave"
              type="multipicklist"
              v-else />
          </div>
        </div>

        <!-- Finality, Manager -->
        <div class="row">
          <!-- Finality -->
          <div class="slds-form-element col slds-size--medium">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :tooltip="$t('campaignDesigner.campaignSetup.finality.tooltip.sales')"
              tooltip-direction="right"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              ref="data_finality"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="finality" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :value="finality"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              type="picklist"
              v-else />
          </div>

          <!-- Manager -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.manager.label')"
              :options="localConfig.campaignData.manager.options"
              labels-prefix="campaignDesigner.campaignSetup.manager"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="manager" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.manager.label')"
              :options="localConfig.campaignData.manager.options"
              :value="manager"
              labels-prefix="campaignDesigner.campaignSetup.manager"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- Description -->
        <div class="row">
          <!-- Description -->
          <div class="slds-form-element col slds-size--medium">
            <div class="slds-form-element slds-size--medium">
              <text-area
                :label="$t('campaignDesigner.campaignSetup.description')"
                v-if="isCreate || isEdit"
                v-model="description" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.description')"
                :value="description"
                type="text"
                v-else />
            </div>
          </div>
        </div>
      </card>

      <!-- Contact Center -->
      <card additional-classes="slds-card_boundary slds-m-top--small" :title="$t('campaignDesigner.campaignSetup.contactCenter')" v-if="campaignChannel.includes('call')">
        <!-- CSC Engagement Level -->
        <div class="row">
          <div class="slds-form-element slds-size--medium col">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.cscEngagementLevel.label')"
              :options="localConfig.campaignData.cscEngagementLevel.options"
              labels-prefix="campaignDesigner.campaignSetup.cscEngagementLevel"
              ref="cscEngagementLevel"
              searchable
              v-if="isCreate || isEdit"
              v-model="cscEngagementLevel" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.cscEngagementLevel.label')"
              :options="localConfig.campaignData.cscEngagementLevel.options"
              :value="cscEngagementLevel"
              labels-prefix="campaignDesigner.campaignSetup.cscEngagementLevel"
              type="picklist"
              v-else />
          </div>

          <!-- CC Script -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Picklist
              :label="$t('campaignDesigner.campaignSetup.ccScript.label')"
              :options="localConfig.campaignData.ccScript.options"
              :validation="['required']"
              labels-prefix="campaignDesigner.campaignSetup.ccScript"
              ref="data_ccScript"
              searchable
              v-if="isCreate || isEdit"
              v-model="ccScript" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.ccScript.label')"
              :options="localConfig.campaignData.ccScript.options"
              :value="ccScript"
              labels-prefix="campaignDesigner.campaignSetup.ccScript"
              type="picklist"
              v-else />
            </div>
          </div>
        </div>

        <!-- CC Extraction Days Frequency, CC Max Records To Extract -->
        <div class="row">
          <!-- CC Extraction Days Frequency -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ccExtractionDaysFreq')"
                :validation="['positive-number', 'required']"
                ref="data_ccExtractionDaysFreq"
                type="number"
                v-if="isCreate || isEdit"
                v-model="ccExtractionDaysFreq" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ccExtractionDaysFreq')"
                :value="ccExtractionDaysFreq"
                v-else />
            </div>
          </div>

          <!-- CC Max Records To Extract -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ccMaxRecordsToExtract')"
                :validation="['positive-number', 'required']"
                ref="data_ccMaxRecordsToExtract"
                type="number"
                v-if="isCreate || isEdit"
                v-model="ccMaxRecordsToExtract" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ccMaxRecordsToExtract')"
                :value="ccMaxRecordsToExtract"
                v-else />
            </div>
          </div>
        </div>

        <!-- CC Calls Start Date, CC Calls End Date -->
        <div class="row">
          <!-- CC Calls Start Date -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ccCallsStartDate')"
                :validation="[{name: 'is-date-before', param: ccCallsEndDate}, 'required']"
                ref="data_ccCallsStartDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="ccCallsStartDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ccCallsStartDate')"
                :value="ccCallsStartDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- CC Calls End Date -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ccCallsEndDate')"
                :validation="['required']"
                ref="data_ccCallsEndDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="ccCallsEndDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ccCallsEndDate')"
                :value="ccCallsEndDate"
                type="date"
                v-else />
            </div>
          </div>
        </div>
      </card>

      <!-- Paper -->
      <card additional-classes="slds-card_boundary" :title="$t('campaignDesigner.campaignSetup.paper')" v-if="campaignChannel.includes('document')">
        <!-- PP Extraction Days Frequency, PP Max Records To Extract -->
        <div class="row">
          <!-- PP Extraction Days Frequency -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppExtractionDaysFreq')"
                :validation="['positive-number', 'required']"
                ref="data_ppExtractionDaysFreq"
                type="number"
                v-if="isCreate || isEdit"
                v-model="ppExtractionDaysFreq" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppExtractionDaysFreq')"
                :value="ppExtractionDaysFreq"
                v-else />
            </div>
          </div>

          <!-- PP Max Records To Extract -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppMaxRecordsToExtract')"
                :validation="['positive-number', 'required']"
                ref="data_ppMaxRecordsToExtract"
                type="number"
                v-if="isCreate || isEdit"
                v-model="ppMaxRecordsToExtract" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppMaxRecordsToExtract')"
                :value="ppMaxRecordsToExtract"
                v-else />
            </div>
          </div>
        </div>

        <!-- PP Extraction Start Date, PP Extraction End Date -->
        <div class="row">
          <!-- PP Extraction Start Date -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppExtractionStartDate')"
                :validation="[{name: 'is-date-before', param: ppExtractionEndDate}, 'required']"
                ref="data_ppExtractionStartDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="ppExtractionStartDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppExtractionStartDate')"
                :value="ppExtractionStartDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- PP Extraction End Date -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppExtractionEndDate')"
                :validation="['required']"
                ref="data_ppExtractionEndDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="ppExtractionEndDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppExtractionEndDate')"
                :value="ppExtractionEndDate"
                type="date"
                v-else />
            </div>
          </div>
        </div>

        <!-- PP Template File Name, PP Provider Id -->
        <div class="row">
          <!-- PPP Template File Name -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppTemplateFilename')"
                :validation="['required']"
                ref="data_ppTemplateFilename"
                v-if="isCreate || isEdit"
                v-model="ppTemplateFilename" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppTemplateFilename')"
                :value="ppTemplateFilename"
                v-else />
            </div>
          </div>

          <!-- PP Provider Id -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppProviderId')"
                :validation="['required']"
                ref="data_ppProviderId"
                v-if="isCreate || isEdit"
                v-model="ppProviderId" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppProviderId')"
                :value="ppProviderId"
                v-else />
            </div>
          </div>
        </div>

        <!-- PP Url -->
        <div class="row">
          <!-- PP Url -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppUrl1')"
                :validation="['url']"
                ref="data_ppUrl1"
                v-if="isCreate || isEdit"
                v-model="ppUrl1" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppUrl1')"
                :value="ppUrl1"
                v-else />
            </div>

            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppUrl2')"
                :validation="['url']"
                ref="data_ppUrl2"
                v-if="isCreate || isEdit"
                v-model="ppUrl2" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppUrl2')"
                :value="ppUrl2"
                v-else />
            </div>

            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.ppUrl3')"
                :validation="['url']"
                ref="data_ppUrl3"
                v-if="isCreate || isEdit"
                v-model="ppUrl3" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.ppUrl3')"
                :value="ppUrl3"
                v-else />
            </div>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import { validate } from '../../utils/utils'
import CampaignTypeSelector from '../cc/campaign-setup/CampaignTypeSelector'
import Card from '../../slds/Card'
import HasMode from '../../utils/HasMode'
import Input from '../../slds/Input'
import OutputField from '../../slds/OutputField'
import Picklist from '../../slds/Picklist'
import Spinner from '../../Spinner'
import TextArea from '../../slds/TextArea'
export default {
  mixins: [HasMode],
  components: {
    CampaignTypeSelector,
    Card,
    Input,
    OutputField,
    Picklist,
    Spinner,
    TextArea
  },
  props: {
    campaignStatus: String,
    loading: Boolean,
    mode: String
  },
  data () {
    return {
      deduplicationParameter: '',
      brandDisabled: false,
      campaignBrands: [],
      campaignChannel: [],
      campaignType: {},
      ccCallsEndDate: '',
      ccCallsStartDate: '',
      ccdbReportEnabled: false,
      ccdbReportType: '',
      ccExtractionDaysFreq: '',
      ccMaxRecordsToExtract: '',
      ccScript: '',
      cscEngagementLevel: '',
      description: '',
      endDate: '',
      finality: '',
      jeepWave: '',
      localConfig: {},
      manager: '',
      markets: '',
      masterKey: '',
      masterKeys: undefined,
      name: '',
      oldCampaignChannel: [],
      onlyHeader: false,
      ppExtractionDaysFreq: '',
      ppExtractionEndDate: '',
      ppExtractionStartDate: '',
      ppMaxRecordsToExtract: '',
      ppProviderId: '',
      ppTemplateFilename: '',
      ppUrl1: '',
      ppUrl2: '',
      ppUrl3: '',
      recordType: '',
      segmentType: '',
      startDate: '',
      targetSource: ''
    }
  },
  beforeCreate () {
    this.$emit('startLoading')
  },
  created () {
    this.callLoadFromStore()
    this.$emit('endLoading')
  },
  beforeDestroy () {
    this.campaignBrands = undefined
    this.campaignChannel = undefined
    this.campaignType = undefined
    this.ccCallsEndDate = undefined
    this.ccCallsStartDate = undefined
    this.ccdbReportEnabled = undefined
    this.ccdbReportType = undefined
    this.ccExtractionDaysFreq = undefined
    this.ccMaxRecordsToExtract = undefined
    this.ccScript = undefined
    this.cscEngagementLevel = undefined
    this.description = undefined
    this.endDate = undefined
    this.finality = undefined
    this.jeepWave = undefined
    this.localConfig = undefined
    this.manager = undefined
    this.markets = undefined
    this.masterKey = undefined
    this.masterKeys = undefined
    this.name = undefined
    this.oldCampaignChannel = undefined
    this.onlyHeader = undefined
    this.ppExtractionDaysFreq = undefined
    this.ppExtractionEndDate = undefined
    this.ppExtractionStartDate = undefined
    this.ppMaxRecordsToExtract = undefined
    this.ppProviderId = undefined
    this.ppTemplateFilename = undefined
    this.ppUrl1 = undefined
    this.ppUrl2 = undefined
    this.ppUrl3 = undefined
    this.recordType = undefined
    this.segmentType = undefined
    this.startDate = undefined
    this.targetSource = undefined
  },
  methods: {
    async callLoadFromStore () {
      const campaign = this.loadFromStore('campaignData', ['loading', 'oldConfig'])

      if (this.$route.query.type) {
        this.recordType = this.$route.query.type
      }

      this.localConfig = this.$store.getters['campaignDesigner/state'].currentCampaignConfig

      if (this.localConfig.campaignData.type.options.length === 1) {
        const selectedType = this.localConfig.campaignData.type.options[0]
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
          disableOrchestrationFlagEditable: selectedType.disableOrchestrationFlagEditable,
          disableOrchestrationFlagDefault: selectedType.disableOrchestrationFlagDefault
        }
        this.disableOrchestration = selectedType.disableOrchestrationFlagDefault
      }

      if (campaign.config.campaignData && campaign.config.campaignData.type && campaign.config.campaignData.type.selected) {
        const selectedType = campaign.config.campaignData.type.selected
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
          disableOrchestrationFlagEditable: selectedType.disableOrchestrationFlagEditable,
          disableOrchestrationFlagDefault: selectedType.disableOrchestrationFlagDefault
        }
      }
      this.masterKeys = (await this.$store.dispatch('settings/getSettings')).masterKeys
    },
    validate () {
      if (this.isCreate || this.isEdit) {
        return validate(this, 'data', true)
      } else {
        const dateFrom = moment(this.startDate, 'YYYY-MM-DD')
        if (this.campaignStatus === 'Draft' && dateFrom.isBefore(moment(), 'day')) {
          this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('campaignDesigner.campaignSetup.validation.startDateMustBeInFuture'))
        } else {
          return true
        }
      }
    },
    getSectionToSave () {
      const campaignType = {
        options: this.localConfig.campaignData.type.options,
        selected: this.localConfig.campaignData.type.options.find(type => type.code === this.campaignType.value)
      }

      let endDate = ''

      if (moment(this.endDate).isValid()) {
        endDate = moment(this.endDate).format('YYYY-MM-DD')
      }

      let ccCallsStartDate = ''
      let ccCallsEndDate = ''
      let ppExtractionStartDate = ''
      let ppExtractionEndDate = ''

      if (moment(this.ccCallsStartDate).isValid()) {
        ccCallsStartDate = moment(this.ccCallsStartDate).format('YYYY-MM-DD')
      }

      if (moment(this.ccCallsEndDate).isValid()) {
        ccCallsEndDate = moment(this.ccCallsEndDate).format('YYYY-MM-DD')
      }

      if (moment(this.ppExtractionStartDate).isValid()) {
        ppExtractionStartDate = moment(this.ppExtractionStartDate).format('YYYY-MM-DD')
      }

      if (moment(this.ppExtractionEndDate).isValid()) {
        ppExtractionEndDate = moment(this.ppExtractionEndDate).format('YYYY-MM-DD')
      }

      return {
        flat: {
          brands: this.campaignBrands,
          ccdbReportEnabled: this.ccdbReportEnabled,
          ccdbReportType: this.ccdbReportType,
          ccCallsEndDate,
          ccCallsStartDate,
          ccExtractionDaysFreq: this.ccExtractionDaysFreq,
          ccMaxRecordsToExtract: this.ccMaxRecordsToExtract,
          ccScript: this.ccScript || null,
          cscEngagementLevel: this.cscEngagementLevel,
          description: this.description,
          finality: this.finality,
          marketCodes: this.markets,
          endDate,
          jeepWave: this.jeepWave,
          manager: this.manager,
          masterKey: this.masterKey,
          name: this.name,
          onlyHeader: this.onlyHeader,
          ppExtractionDaysFreq: this.ppExtractionDaysFreq,
          ppExtractionEndDate,
          ppExtractionStartDate,
          ppMaxRecordsToExtract: this.ppMaxRecordsToExtract,
          ppProviderId: this.ppProviderId,
          ppTemplateFilename: this.ppTemplateFilename,
          ppUrl1: this.ppUrl1,
          ppUrl2: this.ppUrl2,
          ppUrl3: this.ppUrl3,
          recordType: this.recordType,
          segmentType: this.segmentType,
          type: this.campaignType.value,
          startDate: moment(this.startDate).format('YYYY-MM-DD')
        },
        config: {
          campaignBrands: {
            options: this.localConfig.campaignData.campaignBrands.options,
            selected: this.campaignBrands
          },
          campaignChannel: {
            options: this.localConfig.campaignData.campaignChannel.options,
            selected: this.campaignChannel
          },
          ccdbReportType: {
            options: this.localConfig.campaignData.ccdbReportType.options,
            selected: this.ccdbReportType
          },
          ccScript: {
            options: this.localConfig.campaignData.ccScript.options,
            selected: this.ccScript || null
          },
          cscEngagementLevel: {
            options: this.localConfig.campaignData.cscEngagementLevel.options,
            selected: this.cscEngagementLevel
          },
          deduplicationParameter: {
            options: this.localConfig.campaignData.deduplicationParameter.options,
            selected: this.deduplicationParameter
          },
          description: this.description,
          finality: {
            options: this.localConfig.campaignData.finality.options,
            selected: this.finality
          },
          jeepWave: {
            options: this.localConfig.campaignData.jeepWave.options,
            selected: this.jeepWave
          },
          manager: {
            options: this.localConfig.campaignData.manager.options,
            selected: this.manager
          },
          markets: {
            options: this.localConfig.campaignData.markets.options,
            selected: this.markets
          },
          masterKey: {
            selected: this.masterKey
          },
          segmentType: {
            options: this.localConfig.campaignData.segmentType.options,
            selected: this.segmentType
          },
          targetSource: {
            options: this.localConfig.campaignData.targetSource.options,
            selected: this.targetSource
          },
          type: campaignType
        }
      }
    }
  },
  computed: {
    nbsp () {
      return '\xa0'
    }
  },
  watch: {
    campaignBrands (newValue) {
      if (!newValue.includes('057')) {
        this.jeepWave = ''
      }
    },
    campaignChannel (newValue) {
      if (!this.oldCampaignChannel.includes('call') && newValue.includes('call')) {
        this.ccScript = '0'
      } else if (this.oldCampaignChannel.includes('call') && !newValue.includes('call')) {
        this.ccScript = ''
        this.ccExtractionDaysFreq = ''
        this.ccMaxRecordsToExtract = ''
        this.ccCallsStartDate = null
        this.ccCallsEndDate = null
      }

      if (this.oldCampaignChannel.includes('document') && !newValue.includes('document')) {
        this.ppExtractionDaysFreq = ''
        this.ppMaxRecordsToExtract = ''
        this.ppExtractionStartDate = null
        this.ppExtractionEndDate = null
        this.ppTemplateFilename = ''
        this.ppProviderId = ''
        this.ppUrl1 = ''
        this.ppUrl2 = ''
        this.ppUrl3 = ''
      }
      this.oldCampaignChannel = _.cloneDeep(newValue)
    },
    endDate () {
      if (this.isCreate || this.isEdit) {
        this.$refs.data_startDate.clearValidationError()
      }
    },
    masterKey (value) {
      if (value === '') {
        this.masterKey = null
      }
    },
    targetSource (to) {
      if (to === 'contacts') {
        this.brandDisabled = true
        this.deduplicationParameter = 'dedup_by_contact'
      } else {
        this.brandDisabled = false
        this.deduplicationParameter = ''
      }
    }
  },

}
</script>
<style lang="scss" scoped>
  .row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
  }

  .col {
    flex-grow: 1;
  }

  .type-color-container {
    margin-right: 10px;
  }

  .full-height {
    height: 100%;
    overflow-y: auto;
  }

  Input {
    width: unset !important;
  }
</style>
